import React, {useState, useEffect, useRef} from 'react';
import classnames from 'classnames';
import useProps from '../_util/useProps';
import {Math as CesiumMath} from 'cesium';

const prefix = 'cs-map-navigation';

const useMouse = (props) => {
    const timeRef = useRef(null);

    useEffect(() => {
        return () => {
            onClear();
        }
    }, []);

    // 鼠标按下
    const onMouseDown = (type, event) => {
        onClear();
        timeRef.current = window.setInterval(() => {
            props.callback?.(type, event);
        });
    }

    // 鼠标抬起
    const onMouseUp = () => {
        onClear();
    }

    // 清除定时器
    const onClear = () => {
        if (timeRef.current) {
            window.clearInterval(timeRef.current);
            timeRef.current = null;
        }
    }

    return {
        onMouseDown,
        onMouseUp
    }
}

// 视角移动
const useViewMove = () => {
    const {map} = useProps();

    const {onMouseDown, onMouseUp} = useMouse({
        callback: (type) => {
            // 获取相机到椭球表面的距离
            const cameraHeight = map.scene.globe.ellipsoid.cartesianToCartographic(map.camera.position).height;

            // 根据相机到椭球表面的距离，计算移动速度
            const moveRate = cameraHeight /  200.0;

            map.camera[type](moveRate);
        }
    });


    return {
        onMouseDown,
        onMouseUp
    }
}

// 角度
const Angle = () => {
    const {map} = useProps();
    const barRef = useRef();

    // // 监听角度变化
    // useEffect(() => {
    //     const move = ()=> {
    //         // 将角度转换为弧度
    //         const rotation = CesiumMath.toDegrees(map.camera.pitch).toFixed(2);
    //         let radians = (rotation * Math.PI) / 180;
    //
    //         // 获取元素中心点位置
    //         const rect = domRef.current.getBoundingClientRect();
    //         const centerX = rect.width / 2;
    //         const centerY = rect.height / 2;
    //
    //         let distance = rect.width - 6;
    //
    //         // 计算目标点的x和y坐标
    //         let left = centerX + distance * Math.cos(radians);
    //         let top = centerY + (distance / 2) * Math.sin(radians);
    //
    //         barRef.current.style.left = left + "px";
    //         barRef.current.style.top = top + "px";
    //
    //     }
    //
    //     map.scene.postRender.addEventListener(move)
    //     return () => {
    //         map.scene.postRender.removeEventListener(move)
    //     }
    // }, []);

    const domRef = useRef(null);

    const onMouseDown = (event) => {
        event.preventDefault();

        let startX = event.clientX;
        let startY = event.clientY;

        const handleRotate = (e) => {
            const curX = e.clientX;
            const curY = e.clientY;
            const rect = domRef.current.getBoundingClientRect();
            // 获取元素中心点位置
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;

            // 旋转前的角度
            const rotateDegreeBefore =
                Math.atan2(startY - centerY, startX - centerX) / (Math.PI / 180);

            // 旋转后的角度
            const rotateDegreeAfter =
                Math.atan2(curY - centerY, curX - centerX) / (Math.PI / 180);

            // 获取旋转的角度值
            let nowRotate = (rotateDegreeAfter - rotateDegreeBefore);
            const radians = CesiumMath.toRadians(nowRotate);
            map.camera.lookUp(radians);
            startX = curX;
            startY = curY;
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleRotate);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleRotate);
        document.addEventListener('mouseup', handleMouseUp);
    }

    return (
        <div ref={domRef} className={classnames(`${prefix}-angle`)}>
            <div
                ref={barRef}
                // onMouseDown={onMouseDown}
                className={classnames(`${prefix}-angle-bar`)}
                style={{left: 58, top: 5}}
            />
        </div>
    )
}

// 缩放
const Zoom = () => {
    const {onMouseDown, onMouseUp} = useViewMove();

    return (
        <div className={classnames(`${prefix}-zoom`)}>
            <div
                className={classnames(`${prefix}-zoom-in`)}
                onMouseDown={() => onMouseDown('moveForward')}
                onMouseUp={() => onMouseUp('moveForward')}
            />

            <div className={classnames(`${prefix}-zoom-bar`)}/>

            <div
                className={classnames(`${prefix}-zoom-out`)}
                onMouseDown={() => onMouseDown('moveBackward')}
                onMouseUp={() => onMouseUp('moveBackward')}
            />
        </div>
    )
}

// 箭头
const Arrows = () => {
    const {onMouseDown, onMouseUp} = useViewMove();
    return (
        <>
            <div className={classnames(`${prefix}-arrows`)}/>

            <div
                className={classnames(`${prefix}-arrows-e-active`)}
                onMouseDown={() => onMouseDown('moveRight')}
                onMouseUp={() => onMouseUp('moveRight')}
            />

            <div
                className={classnames(`${prefix}-arrows-n-active`)}
                onMouseDown={() => onMouseDown('moveUp')}
                onMouseUp={() => onMouseUp('moveUp')}
            />

            <div
                className={classnames(`${prefix}-arrows-s-active`)}
                onMouseDown={() => onMouseDown('moveDown')}
                onMouseUp={() => onMouseUp('moveDown')}
            />

            <div
                className={classnames(`${prefix}-arrows-w-active`)}
                onMouseDown={() => onMouseDown('moveLeft')}
                onMouseUp={() => onMouseUp('moveLeft')}
            />
        </>
    )
}

// 指南针
const Compass = () => {
    const {map} = useProps();
    const [rotation, setRotation] = useState(0);

    // 监听角度变化
    useEffect(() => {
        const move = ()=> {
            setRotation(CesiumMath.toDegrees(map.camera.heading).toFixed(2));
        }

        map.scene.postRender.addEventListener(move)
        return () => {
            map.scene.postRender.removeEventListener(move)
        }
    }, []);

    const domRef = useRef(null);

    const onMouseDown = (event) => {
        event.preventDefault();

        let startX = event.clientX;
        let startY = event.clientY;

        const handleRotate = (e) => {
            const curX = e.clientX;
            const curY = e.clientY;
            const rect = domRef.current.getBoundingClientRect();
            // 获取元素中心点位置
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;

            // 旋转前的角度
            const rotateDegreeBefore =
                Math.atan2(startY - centerY, startX - centerX) / (Math.PI / 180);

            // 旋转后的角度
            const rotateDegreeAfter =
                Math.atan2(curY - centerY, curX - centerX) / (Math.PI / 180);

            // 获取旋转的角度值
            let nowRotate = (rotateDegreeAfter - rotateDegreeBefore);
            const radians = CesiumMath.toRadians(nowRotate);
            map.camera.twistRight(radians);
            startX = curX;
            startY = curY;
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleRotate);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleRotate);
        document.addEventListener('mouseup', handleMouseUp);
    }

    return (
        <div className={classnames(`${prefix}-compass`)}>
            <div
                ref={domRef}
                className={classnames(`${prefix}-compass-ring`)}
                onMouseDown={onMouseDown}
                style={{transform: `rotate(${rotation}deg)`}}
            >
                <div
                    className={classnames(`${prefix}-compass-ring-center`)}
                />
            </div>
        </div>
    )
}

const Navigation = (props) => {
    const {className, style} = props;
    return (
        <div className={classnames(`${prefix}`, className)} style={style}>
            <Compass />
            <Arrows />
            <Zoom />
        </div>
    )
}

export default Navigation;
